/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-04 20:21:26
 **/

import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import LottiePlayer from "@rsupport/lottieplayer";
import { useAtomValue } from "jotai";

import { ChronoUtils, Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import {
	Button,
	Column,
	Icon,
	Row,
	Tooltip,
	Typography,
	TypographyTooltip,
	useToast
} from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { RestAPIError } from "@/frameworks/libraries/restservice";

import { PhoneCallAtoms, usePhoneCall, usePopover, useSidebarActions } from "@/frameworks/core/hooks";
import { Avatar, CallAvatar, UserVideoWithAvatar } from "@/frameworks/core/components";
import { APIErrorCode, POPOVER_ID, SidebarID } from "@/frameworks/core/commons";
import { useChatRoomActions } from "@/frameworks/core/chat";
import { orderCallUser, removeMeFromList } from "@/frameworks/core/utils";
import { usePersistActions } from "@/frameworks/core/persistence";
import { JotaiStore } from "@/frameworks/libraries";
import voiceRecordingCall from "@/assets/lotties/media/voice_recording_call.json";
import { useChatDataStore } from "@/modules/chat/store";
import { AddCalleesPopup, DialingState, ScreenShareVideo } from "../../commons";
// const LOGTAG = "#PhoneCall#";

const optionsRecording = {
	loop: true,
	autoplay: false,
	animationData: voiceRecordingCall,
	rendererSettings: {
		preserveAspectRatio: "xMidYMid slice"
	}
};

export default function PhoneCall({ callID, callerID }: PushServices.Call.PhoneCallPopupItem) {
	const { users } = usePersistActions();
	const toast = useToast();
	const sidebarActions = useSidebarActions();
	const { colors } = useTheme();
	const popover = usePopover();
	const [isExpand, setIsExpand] = useState(false);
	const chatRoomActions = useChatRoomActions();
	const showFullSizeChat = useChatDataStore((state) => state.show_fullsize_chat);
	const phoneCall = usePhoneCall();
	const call = phoneCall.state.call(callID)[0];
	const callUsers = phoneCall.state.users(callID)[0];
	const callStatus = phoneCall.state.status(callID)[0];
	/**
	 * Update 02/01/2025: When you end a call, the profile of the person who has already left should not appear on the screen.
	 * @ticket: 231955
	 */
	const callUserIDList = useMemo(
		() =>
			(callUsers &&
				Object.values(callUsers)
					.filter((user) => user.status !== "ENDED")
					.map((user) => user.userId ?? "")) ||
			[],
		[callUsers]
	);
	const userIdInCallList = callUserIDList?.length === 0 ? [Configure.userID] : callUserIDList;
	const requestUserList =
		call.callUsers &&
		Object.values(call.callUsers)
			.filter((user) => user.status === "REQUESTED")
			.map((user) => user.userId ?? "");
	const participatedUserList = phoneCall.state.participatedUserList(callID);
	const userList = callStatus === "END" ? userIdInCallList : participatedUserList;
	const myInfo = useMemo(() => callUsers && callUsers?.[Configure.userID], [callUsers]);
	const [isFirstCamera, setIsFirstCamera] = useState(myInfo?.media?.video === "on");
	const userWithScreenOn =
		callUsers &&
		Object.values(callUsers)
			.filter((user) => user.status !== "ENDED")
			.filter((user) => user.media?.screenShare)
			.filter((user) => user.userId !== Configure.userID);
	const userWithVideoOn =
		callUsers &&
		Object.values(callUsers)
			.filter((user) => user.status !== "ENDED")
			.filter((user) => user.media?.video === "on");

	useEffect(() => {
		if (userWithVideoOn?.length === 0) {
			setIsExpand(false);
		} else if (userWithVideoOn?.length > 0) {
			setIsExpand(true);
		}
	}, [userWithVideoOn?.length]);

	const openDM = async () => {
		try {
			if (showFullSizeChat) {
				await sidebarActions.hideAll("left");
				await sidebarActions.show(SidebarID.Chat);
			}
			const data = await chatRoomActions.dm.create({
				userIDs: [...userList]
			});
			if (!data) return;
			chatRoomActions.common.open({ channelID: data.id, type: "DM" });
		} catch (err) {
			const error = err as RestAPIError;
			switch (error.code) {
				case APIErrorCode.EXIST_SAME_CHAT_MEMBER: {
					const { roomId } = error.data.payload;
					chatRoomActions.common.open({ channelID: roomId, type: "DM" });
				}
			}
		}
	};
	const endScreenShare = () => {
		phoneCall.screen_update(callID, myInfo?.userId ?? "", "stop");
	};

	useEffect(() => {
		if (call.status === "END") {
			// phoneCall.popup.hide(callID);
		}
	}, [call]);

	const handleAddUser = async (ev: React.MouseEvent<HTMLButtonElement>) => {
		popover.show({
			popoverID: POPOVER_ID.ADD_CALL_USER,
			content: <AddCalleesPopup callID={callID} />,
			anchorEl: ev.currentTarget,
			direction: {
				x: "right",
				y: "top"
			}
		});
	};

	const handleExpand = () => {
		setIsExpand((prev) => !prev);
		setIsFirstCamera(true);
	};

	const handleCamera = () => {
		phoneCall.camera_update(callID, myInfo?.userId ?? "", myInfo?.media?.video === "on" ? "off" : "on");
		setIsFirstCamera(true);
	};

	const userInfo = useMemo(() => {
		return removeMeFromList(userList)?.[0];
	}, [userList]);

	const recordTime = useAtomValue(PhoneCallAtoms.RecordTime(callID ?? ""), { store: JotaiStore });
	return (
		<Container countUser={userList.length} gap={5}>
			<Row horizonAlign="distribute" style={{ width: "100%" }} verticalAlign="bottom">
				<Maybe
					test={removeMeFromList(userList).length === 1}
					fallback={
						<Row style={{ position: "relative" }} gap={12}>
							<Row
								style={{
									backgroundColor: colors.accent_40_100,
									width: "40px",
									height: "40px",
									borderRadius: "50%"
								}}
								verticalAlign="center"
								horizonAlign="center"
							>
								<Icon name="People24" size={20} fill="white" />
							</Row>
							<Column>
								<Row>
									<TypographyTooltip.div
										tooltipProps={{
											hasIcon: false
										}}
										componentProps={{
											typoSize: "Title1_B",
											typoColor: "neutral_00_100",
											ellipsis: true,
											maxLine: 1,
											stretch: true,
											align: "left"
										}}
									>
										{removeMeFromList(userList)
											?.map(
												(userID) =>
													callUsers?.[userID].name ?? callUsers?.[userID]?.callNumber ?? ""
											)
											.join(", ")}
									</TypographyTooltip.div>
								</Row>
								<Typography typoColor="neutral_00_68" typoSize="Body3_MD">
									{_T("{{count}}명 참여중", { count: removeMeFromList(userList)?.length ?? 0 })}
								</Typography>
							</Column>
						</Row>
					}
				>
					<Row style={{ position: "relative" }} gap={12}>
						<Avatar id={(userInfo && userInfo) || ""} kind="people_40" />
						<Column>
							<TypographyTooltip.div
								tooltipProps={{
									hasIcon: false
								}}
								componentProps={{
									typoSize: "Title1_B",
									typoColor: "neutral_00_100",
									ellipsis: true,
									maxLine: 1,
									stretch: true,
									align: "left"
								}}
							>
								{(userInfo && users.get_user(userInfo)?.name) ?? callUsers?.[userInfo]?.callNumber}
							</TypographyTooltip.div>
							<Typography typoColor="neutral_00_68" typoSize="Body3_MD">
								{userInfo && users.get_user(userInfo)?.username}
							</Typography>
						</Column>
					</Row>
				</Maybe>
				<Row style={{ paddingRight: "8px" }}>
					<DialingState call={call} callID={callID} typoSize="Title3" typoColor="success_20_100" />
				</Row>
			</Row>
			<Row style={{ paddingTop: 8, width: "100%" }} gap={8} horizonAlign="distribute" verticalAlign="center">
				<Row gap={4} style={{ height: "100%" }} verticalAlign="center">
					<Maybe test={isFirstCamera}>
						<Button.Icon
							name={isExpand ? "Up16" : "Down16"}
							size="large"
							filled="transparent"
							fill="neutral_00_100"
							onClick={handleExpand}
						/>
						<Delimiter />
						<Maybe.Fallback>
							<Delimiter mLeft={40} />
						</Maybe.Fallback>
					</Maybe>
					<Button
						onClick={handleCamera}
						style={{
							backgroundColor: "transparent",
							borderRadius: "20px",
							width: 32,
							height: 32
						}}
					>
						<Icon
							style={{ marginTop: 5 }}
							name={myInfo?.media?.video === "on" ? "CamOn24" : "CamOff24"}
							size={24}
							color={colors.neutral_00_100}
						/>
					</Button>
					<Button
						onClick={() =>
							phoneCall.microphone_update(
								callID,
								myInfo?.userId ?? "",
								myInfo?.media?.audio === "on" ? "off" : "on"
							)
						}
						style={{
							backgroundColor: "transparent",
							borderRadius: "20px",
							width: 32,
							height: 32
						}}
					>
						<Icon
							style={{ marginTop: 5 }}
							name={myInfo?.media?.audio === "on" ? "MicOn24" : "MicOff24"}
							size={24}
							color={colors.neutral_00_100}
						/>
					</Button>
					{/* feature not yet developed, will show temporary UI */}
					<Button
						style={{
							backgroundColor: "transparent",
							borderRadius: "20px",
							width: 32,
							height: 32
						}}
					>
						<Icon style={{ marginTop: 5 }} name={"SpeakerOn24"} size={24} color={colors.neutral_00_100} />
					</Button>

					<StyledLottieButton
						disabled={callStatus !== "ONGOING"}
						onClick={() => {
							if (myInfo?.media?.isRecording) {
								phoneCall.record_update(callID, "stop");
								toast.show({
									type: "system",
									message: _T("통화 녹음이 종료되었습니다. 저장된 파일은 파일함에서 확인 가능합니다.")
								});
							} else {
								phoneCall.record_update(callID, "start");
								toast.show({ type: "system", message: _T("통화 녹음이 시작되었습니다.") });
							}
						}}
						style={{
							backgroundColor: "transparent",
							borderRadius: "20px",
							width: 32,
							height: 32,
							opacity: callStatus !== "ONGOING" ? 0.4 : 1
						}}
					>
						{myInfo?.media?.isRecording ? (
							<Tooltip
								message={_T("녹음중 {{time}}", {
									time: ChronoUtils.calculateRecording(recordTime ?? 0)
								})}
								hasIcon={false}
							>
								<LottiePlayer
									isStopped={!myInfo?.media?.isRecording}
									isPaused={!myInfo?.media?.isRecording}
									options={optionsRecording}
									height={24}
									width={24}
								/>
							</Tooltip>
						) : (
							<LottiePlayer
								isStopped={!myInfo?.media?.isRecording}
								isPaused={!myInfo?.media?.isRecording}
								options={optionsRecording}
								height={24}
								width={24}
							/>
						)}
					</StyledLottieButton>
					<Button.Icon
						name="ScreenShare24"
						size="large"
						filled="transparent"
						fill="neutral_00_100"
						disabled={callStatus !== "ONGOING"}
						onClick={() => phoneCall.screen_update(callID, myInfo?.userId ?? "", "start")}
					/>
					{callerID === Configure.userID && (
						<Button.Icon
							name="CallAdd24"
							size="large"
							filled="transparent"
							fill="neutral_00_100"
							disabled={userList.length === 4 || callStatus === "READY"}
							onClick={handleAddUser}
						/>
					)}
					<Delimiter />
					<Button.Icon
						name="Chat0124"
						size="large"
						filled="transparent"
						fill="neutral_00_100"
						disabled={
							callUsers &&
							Object.values(callUsers)?.filter((user) => user.status === "REQUESTED")?.length > 0
						}
						onClick={async () => await openDM()}
					/>
				</Row>
				<Button.Icon
					name="CallEnd24"
					filled="transparent"
					fill="neutral_00_100"
					size="large"
					style={{ width: "96px", height: "40px", backgroundColor: colors.error_60_100 }}
					onClick={() => {
						phoneCall.call_end(callID);
					}}
				/>
				{!isExpand &&
					callUsers &&
					Object.values(callUsers).map((user) => (
						<UserVideoWithAvatar
							key={`i-${user.userId}`}
							kind="people_80"
							style={{ display: "none" }}
							callID={callID}
							userID={user.userId || ""}
						/>
					))}
			</Row>
			<Column gap={10} style={{ width: "100%" }}>
				{Object.keys(removeMeFromList(userList)).length >= 2 && !isExpand && (
					<Row gap={20} style={{ paddingLeft: "48px", paddingTop: 8 }}>
						{orderCallUser(removeMeFromList(userList), call.callerId, requestUserList)?.map((userID) => {
							const userInfo = callUsers?.[userID];
							return (
								<Column horizonAlign="center" key={userID} css={{ maxWidth: "72px" }}>
									<CallAvatar
										id={userID ?? ""}
										size="small"
										useStatus={false}
										callType={
											userInfo?.status === "REQUESTED"
												? "calling"
												: userInfo?.status === "REJECTED"
													? "no_response"
													: "none"
										}
									/>
									<TypographyTooltip.p
										tooltipProps={{
											hasIcon: false
										}}
										componentProps={{
											typoSize: "Body5_SB",
											typoColor: "neutral_00_68",
											ellipsis: true,
											maxLine: 1,
											stretch: true,
											align: "center"
										}}
									>
										{userInfo.name}
									</TypographyTooltip.p>
								</Column>
							);
						})}
					</Row>
				)}
				<Maybe test={isExpand}>
					<Row gap={16} horizonAlign="center" style={{ width: "100%", paddingTop: 8 }}>
						{orderCallUser(userList, call.callerId, requestUserList)?.map((userID) => {
							const userInfo = callUsers?.[userID];
							return (
								<Row key={userID} gap={16}>
									{
										<UserVideoWithAvatar
											kind="people_80"
											style={{ width: "192px", height: "108px" }}
											userID={userID ?? ""}
											callID={callID}
											callType={
												userID === Configure.userID
													? "none"
													: userInfo?.status === "REQUESTED"
														? "calling"
														: userInfo?.status === "REJECTED"
															? "no_response"
															: "none"
											}
											isExpandIcon={true}
										/>
									}
								</Row>
							);
						})}
					</Row>
				</Maybe>
				<Maybe
					test={userWithScreenOn?.length > 0}
					fallback={
						<Maybe test={myInfo?.media?.screenShare}>
							<Row style={{ width: "100%", paddingTop: 12 }} horizonAlign="center">
								<Column
									style={{
										width: "392px",
										height: "220px",
										backgroundColor: colors.neutral_60_100,
										borderRadius: "8px"
									}}
									gap={8}
									horizonAlign="center"
									verticalAlign="center"
								>
									<Row gap={6}>
										<Icon name="ScreenShare24" size={20} fill={colors.neutral_00_100} />
										<Typography typoColor="neutral_00_100" typoSize="Title3">
											{_T("내 화면 공유 중")}
										</Typography>
									</Row>
									<Button filled="neutral" style={{ borderRadius: "100px" }} onClick={endScreenShare}>
										{_T("공유종료")}
									</Button>
								</Column>
							</Row>
						</Maybe>
					}
				>
					<Row style={{ width: "100%", paddingTop: 12 }} horizonAlign="center">
						<ScreenShareVideo
							style={{ width: "392px", height: "220px" }}
							userID={userWithScreenOn?.[0]?.userId ?? ""}
							callID={callID}
							isExpandIcon={true}
						/>
					</Row>
				</Maybe>
			</Column>
		</Container>
	);
}

const Container = styled(Column)<{ countUser: number }>`
	width: ${({ countUser }) =>
		countUser === 2 ? "520px" : countUser === 3 ? "636px" : countUser === 4 ? "840px" : "520px"};
	background-color: ${({ theme }) => theme.colors.neutral_70_100};
	border-radius: 12px;
	padding: 12px;
`;

const Delimiter = styled.div<{ mLeft?: number }>`
	height: 14px;
	border-right: 1px solid ${({ theme }) => theme.colors.neutral_00_32};
	margin: 0 4px;
	margin-left: ${({ mLeft }) => `${mLeft}px`};
`;

const StyledLottieButton = styled(Button)<{ disabled: boolean }>`
	path {
		fill: ${({ disabled }) => (disabled ? "" : "white")};
		opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
	}
`;
